import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  getElectricityCost,
  getGasCost,
  getWaterCost,
  DashboardConfig,
  DashboardConfigChannel,
  CostDataPoint,
} from '../../services/api.ts';
import { nowISOString, lastMonthISOString, twoMonthsAgoISOString } from '../../utils/utils.ts';

function sumCosts(data: CostDataPoint[]): number {
  return data.reduce((acc, d) => acc + Number(d.cost_gbp), 0);
}

type SummaryRow = {
  parameter:     string;
  currentValue:  number;
  previousValue: number;
};

export type SummaryData = {
  electricity: { current: number; previous: number };
  gas:         { current: number; previous: number };
  water:       { current: number; previous: number };
};

export type PerOsmCost = {
  osm_name: string;
  utility:  'electricity' | 'gas' | 'water';
  current:  number;
  previous: number;
};

type Props = {
  configs:      DashboardConfig[];
  channels:     DashboardConfigChannel[];
  onDataLoaded?: (summary: SummaryData, perOsm: PerOsmCost[]) => void;
};

const headCellSx = {
  fontFamily: "'Syne', sans-serif",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: 'var(--color-text-secondary)',
  background: 'transparent',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  py: 1.25,
};

const bodyCellSx = {
  fontFamily: "'DM Mono', monospace",
  fontSize: 13,
  color: 'var(--color-text-primary)',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
  py: 1.25,
  background: 'transparent',
};

export default function SummaryTable({ configs, channels, onDataLoaded }: Props) {
  const [rows, setRows]       = React.useState<SummaryRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!configs.length) return;

    async function load() {
      try {
        const now       = nowISOString();
        const lastMonth = lastMonthISOString();
        const twoAgo    = twoMonthsAgoISOString();
        const perOsm: PerOsmCost[] = [];

        // ── Electricity ───────────────────────────────────────────────────────
        const elecResults = await Promise.all(
          configs.map(c => Promise.all([
            getElectricityCost(c.osm_name, lastMonth, now).catch(() => [] as CostDataPoint[]),
            getElectricityCost(c.osm_name, twoAgo, lastMonth).catch(() => [] as CostDataPoint[]),
          ]))
        );
        let elecCurrent = 0, elecPrevious = 0;
        elecResults.forEach(([cur, prev], i) => {
          const c = sumCosts(cur), p = sumCosts(prev);
          elecCurrent += c; elecPrevious += p;
          perOsm.push({ osm_name: configs[i].osm_name, utility: 'electricity', current: c, previous: p });
        });

        // ── Gas — all channels across all configs ─────────────────────────────
        const gasChannels = channels.filter(ch => ch.type === 'gas');
        const gasResults  = await Promise.all(
          gasChannels.map(ch => {
            const cfg = configs.find(c => c.id === ch.config_id);
            if (!cfg) return Promise.resolve([[],[]] as [CostDataPoint[], CostDataPoint[]]);
            return Promise.all([
              getGasCost(cfg.osm_name, lastMonth, now, ch.channel as 'CNT1'|'CNT2', ch.pulse_div, ch.calorific_kwh ?? 11.1354133333).catch(() => [] as CostDataPoint[]),
              getGasCost(cfg.osm_name, twoAgo, lastMonth, ch.channel as 'CNT1'|'CNT2', ch.pulse_div, ch.calorific_kwh ?? 11.1354133333).catch(() => [] as CostDataPoint[]),
            ]);
          })
        );
        let gasCurrent = 0, gasPrevious = 0;
        gasResults.forEach(([cur, prev], i) => {
          const c = sumCosts(cur), p = sumCosts(prev);
          gasCurrent += c; gasPrevious += p;
          const cfg = configs.find(co => co.id === gasChannels[i].config_id);
          if (cfg) perOsm.push({ osm_name: cfg.osm_name, utility: 'gas', current: c, previous: p });
        });

        // ── Water — all channels across all configs ───────────────────────────
        const waterChannels = channels.filter(ch => ch.type === 'water');
        const waterResults  = await Promise.all(
          waterChannels.map(ch => {
            const cfg = configs.find(c => c.id === ch.config_id);
            if (!cfg) return Promise.resolve([[],[]] as [CostDataPoint[], CostDataPoint[]]);
            return Promise.all([
              getWaterCost(cfg.osm_name, lastMonth, now, ch.channel as 'CNT1'|'CNT2', ch.pulse_div).catch(() => [] as CostDataPoint[]),
              getWaterCost(cfg.osm_name, twoAgo, lastMonth, ch.channel as 'CNT1'|'CNT2', ch.pulse_div).catch(() => [] as CostDataPoint[]),
            ]);
          })
        );
        let waterCurrent = 0, waterPrevious = 0;
        waterResults.forEach(([cur, prev], i) => {
          const c = sumCosts(cur), p = sumCosts(prev);
          waterCurrent += c; waterPrevious += p;
          const cfg = configs.find(co => co.id === waterChannels[i].config_id);
          if (cfg) perOsm.push({ osm_name: cfg.osm_name, utility: 'water', current: c, previous: p });
        });

        setRows([
          { parameter: 'Electricity', currentValue: elecCurrent,  previousValue: elecPrevious  },
          { parameter: 'Gas',         currentValue: gasCurrent,   previousValue: gasPrevious   },
          { parameter: 'Water',       currentValue: waterCurrent, previousValue: waterPrevious },
        ]);

        onDataLoaded?.(
          {
            electricity: { current: elecCurrent,  previous: elecPrevious  },
            gas:         { current: gasCurrent,   previous: gasPrevious   },
            water:       { current: waterCurrent, previous: waterPrevious },
          },
          perOsm,
        );
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [configs, channels]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
      <CircularProgress size={24} sx={{ color: '#1D9E75' }} />
    </Box>
  );

  if (error) return (
    <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#993C1D' }}>
      Error: {error}
    </Typography>
  );

  return (
    <TableContainer sx={{ background: 'transparent' }}>
      <Table aria-label="summary table" size="small">
        <TableHead>
          <TableRow>
            <TableCell align="left"   sx={headCellSx}>Utility</TableCell>
            <TableCell align="right"  sx={headCellSx}>Last 30 Days</TableCell>
            <TableCell align="right"  sx={headCellSx}>Prev 30 Days</TableCell>
            <TableCell align="center" sx={headCellSx}>Trend</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => {
            const improved = row.currentValue <= row.previousValue;
            return (
              <TableRow key={row.parameter} sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell align="left" sx={{ ...bodyCellSx, color: 'var(--color-text-secondary)', fontFamily: "'Syne', sans-serif", fontWeight: 500, fontSize: 12 }}>
                  {row.parameter}
                </TableCell>
                <TableCell align="right" sx={bodyCellSx}>
                  £{row.currentValue.toFixed(2)}
                </TableCell>
                <TableCell align="right" sx={{ ...bodyCellSx, color: 'var(--color-text-secondary)' }}>
                  £{row.previousValue.toFixed(2)}
                </TableCell>
                <TableCell align="center" sx={bodyCellSx}>
                  {improved
                    ? <TrendingDownIcon sx={{ fontSize: 18, color: '#1D9E75' }} />
                    : <TrendingUpIcon   sx={{ fontSize: 18, color: '#993C1D' }} />
                  }
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

